import {
  Alert as MuiAlert,
  Box,
  Button,
  CircularProgress,
  Container,
  MenuItem,
  Paper,
  TextField,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AlertDrawer from "@/components/Alerts/AlertDrawer.tsx";
import AlertsTable from "@/components/Alerts/AlertsTable.tsx";
import NotificationsButton from "@/components/Alerts/NotificationsButton.tsx";
import { useAlerts } from "@/hooks/useAlerts.ts";
import { ALERT_PERIODS, DEFAULT_ALERT_PERIOD, useAlertsUrlState } from "@/hooks/useAlertsUrlState.ts";
import { cardStyles, colors, eventsLightTheme, toggleButtonGroupStyle } from "@/theme/eventsTokens.ts";
import {
  ALERTS_SUBTITLE,
  LOAD_ALERTS_FAILED,
  NO_ALERTS,
  NO_MATCHING_ALERTS,
  RULE_FILTER_LABELS,
} from "@/utils/alertCopy.ts";
import type { Alert, AlertFilters, AlertPeriodDays } from "@/types";

const ALL_RULES = "all";

function AlertsFilterBar({
  filters,
  onSetPeriod,
  onSetRule,
}: {
  readonly filters: AlertFilters;
  readonly onSetPeriod: (days: AlertPeriodDays) => void;
  readonly onSetRule: (rule: string | null) => void;
}) {
  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        aria-label="Period"
        value={filters.period_days}
        onChange={(_event, value: AlertPeriodDays | null) => {
          if (value !== null) onSetPeriod(value);
        }}
        sx={toggleButtonGroupStyle}
      >
        {ALERT_PERIODS.map((days) => (
          <ToggleButton key={days} value={days}>
            {`${days} days`}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <TextField
        select
        size="small"
        label="Rule"
        value={filters.rule ?? ALL_RULES}
        onChange={(event) => onSetRule(event.target.value === ALL_RULES ? null : event.target.value)}
        sx={{ minWidth: 280 }}
      >
        <MenuItem value={ALL_RULES}>All rules</MenuItem>
        {Object.entries(RULE_FILTER_LABELS).map(([rule, label]) => (
          <MenuItem key={rule} value={rule}>
            {label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}

function LogBody({
  isLoading,
  error,
  rows,
  isFiltered,
  onOpen,
}: {
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly rows: readonly Alert[];
  readonly isFiltered: boolean;
  readonly onOpen: (id: string) => void;
}) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error && rows.length === 0) return <MuiAlert severity="error">{LOAD_ALERTS_FAILED}</MuiAlert>;
  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: colors.textSecondary, p: 3 }}>
        {isFiltered ? NO_MATCHING_ALERTS : NO_ALERTS}
      </Typography>
    );
  }
  return <AlertsTable rows={rows} onOpen={onOpen} />;
}

export default function AlertsPage() {
  const url = useAlertsUrlState();
  const { data, error, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useAlerts(url.filters);
  const rows = (data?.pages ?? []).flatMap((page) => page.items);
  const isFiltered = url.filters.rule !== null || url.filters.period_days !== DEFAULT_ALERT_PERIOD;

  return (
    <ThemeProvider theme={eventsLightTheme}>
      <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography component="h1" sx={{ fontSize: "1.125rem", fontWeight: 600, color: colors.textPrimary }}>
              Alerts
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.5, mb: 3 }}>
              {ALERTS_SUBTITLE}
            </Typography>
          </Box>
          <NotificationsButton />
        </Box>
        <AlertsFilterBar filters={url.filters} onSetPeriod={url.setPeriod} onSetRule={url.setRule} />
        <Paper elevation={0} sx={{ ...cardStyles.default, overflow: "hidden" }}>
          <LogBody isLoading={isLoading} error={error} rows={rows} isFiltered={isFiltered} onOpen={url.openAlert} />
        </Paper>
        {error && rows.length > 0 && (
          <MuiAlert severity="error" sx={{ mt: 2 }}>
            {LOAD_ALERTS_FAILED}
          </MuiAlert>
        )}
        {hasNextPage && (
          <Button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} sx={{ mt: 2 }}>
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        )}
        <AlertDrawer alertId={url.alertId} onClose={url.closeAlert} onOpenAlert={url.openAlert} />
      </Container>
    </ThemeProvider>
  );
}
